import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, Button, Grid } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import AttachFile from "../../../components/Comman/AttachFile";
import { LabelCompo } from "../../../components/Comman/Label";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import {
  errorAtValidationMsg,
  importTxt,
  noInternetMsg,
  selectExcelFileMsg,
  serverMsg,
  validatedSuccessfullyMsg,
} from "../../../config/messageconstant";
import { apiPostForImage } from "../../../utils/api_service";
import { showLoador, showNotification } from "../../Landing/Landing.slice";
import { RU_TARGET_IMPORT_API } from "../constants/apiPaths";
import { importPopupStyles as styles } from "../styles/importPopupStyles";
import {
  hasValidationErrors,
  normalizeValidatedList,
} from "../utils/validationHelpers";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = ({ children, onClose, ...other }) => (
  <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
    {children}
    {onClose ? (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={styles.closeIconButton}
      >
        <CloseIcon />
      </IconButton>
    ) : null}
  </DialogTitle>
);

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const ImportRuWiseTargetPopUp = ({
  handleCloses,
  open,
  showNotification,
  showLoador,
  dataMethod,
}) => {
  const [fileName, setFileName] = useState("");

  const handleClose = useCallback(() => {
    handleCloses();
  }, [handleCloses]);

  const changeH = useCallback((file) => {
    setFileName(file);
  }, []);

  const onClickViewPreview = useCallback((e, file) => {
    e.stopPropagation();
    const url = URL.createObjectURL(file);
    window.open(url);
  }, []);

  const onClearHandler = useCallback(() => {
    setFileName("");
  }, []);

  const onValidate = useCallback(async () => {
    if (!navigator.onLine) {
      showNotification({ msg: noInternetMsg, severity: "error" });
      return;
    }

    if (!fileName) {
      showNotification({ msg: selectExcelFileMsg, severity: "error" });
      return;
    }

    const formData = new FormData();
    formData.append(RU_TARGET_IMPORT_API.excelFormKey, fileName);

    showLoador({ loador: true });
    try {
      const { data, success } = await apiPostForImage({
        url: RU_TARGET_IMPORT_API.validate,
        postBody: formData,
      });

      if (!success) {
        showNotification({ msg: serverMsg, severity: "error" });
        return;
      }

      if (data?.error) {
        showNotification({
          msg: data?.data?.errorMessage || data?.data?.message || serverMsg,
          severity: "error",
        });
      }

      const recordList = normalizeValidatedList(data?.data);
      const hasErrorRow = hasValidationErrors(recordList);

      if (!data?.error) {
        showNotification(
          hasErrorRow
            ? { msg: errorAtValidationMsg, severity: "error" }
            : { msg: validatedSuccessfullyMsg }
        );
      }

      dataMethod(recordList);
      handleClose();
    } finally {
      showLoador({ loador: false });
    }
  }, [fileName, showLoador, showNotification, dataMethod, handleClose]);

  const isFileSelected = useMemo(() => fileName !== "", [fileName]);

  return (
    <BootstrapDialog
      aria-labelledby="ru-target-import-dialog-title"
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: styles.dialog }}
    >
      <BootstrapDialogTitle
        id="ru-target-import-dialog-title"
        onClose={handleClose}
      >
        {importTxt}
      </BootstrapDialogTitle>

      <DialogContent dividers sx={styles.dialogContent}>
        <Grid
          container
          xs={12}
          sm={12}
          md={12}
          spacing={0.5}
          sx={styles.outerGrid}
        >
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Box sx={styles.contentBox}>
              <Box sx={styles.labelRow}>
                {!isFileSelected && (
                  <>
                    <Box sx={styles.labelInner}>
                      <LabelCompo
                        className="text-black"
                        style={styles.asteriskLabel}
                        label={"*"}
                      />
                      <LabelCompo
                        className="text-black"
                        style={styles.selectFileLabel}
                        label={"Select Excel File :"}
                      />
                    </Box>
                    <Box sx={styles.attachFileBox}>
                      <AttachFile
                        name={"file"}
                        fileName={changeH}
                        sx={{ flexGrow: 1, minWidth: 0 }}
                      />
                    </Box>
                  </>
                )}
              </Box>

              {isFileSelected && (
                <Box sx={styles.fileNameRow}>
                  <TypographyCompo
                    variant="body2"
                    onClick={(e) => onClickViewPreview(e, fileName)}
                    sx={styles.fileNameText}
                  >
                    {fileName.name}
                  </TypographyCompo>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogContent dividers>
        <Grid
          container
          xs={12}
          sm={12}
          md={12}
          spacing={1}
          sx={styles.actionGrid}
        >
          {!isFileSelected && (
            <Grid item xs={12} sm={12} md={12} lg={12} sx={styles.hintTextGrid}>
              <TypographyCompo variant="body2" sx={styles.hintText}>
                {selectExcelFileMsg}
              </TypographyCompo>
            </Grid>
          )}

          {isFileSelected && (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              sx={styles.actionButtonsGrid}
            >
              <Box sx={styles.actionButtonsBox}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<ClearIcon />}
                  sx={styles.clearButton}
                  onClick={onClearHandler}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<CloudUploadIcon sx={styles.uploadIcon} />}
                  sx={styles.validateButton}
                  onClick={onValidate}
                >
                  Validate
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </BootstrapDialog>
  );
};

const mapDispatchToProps = {
  showNotification,
  showLoador,
};

export default connect(null, mapDispatchToProps)(ImportRuWiseTargetPopUp);
